/**
 * PERSO: combinaisons inexistantes (réglage « Combinaisons inexistantes » du sélecteur de variantes, modes
 * « grisées » et « masquées »).
 *
 * Quand on change une option et que la combinaison obtenue n'existe pas (ex. 4.2 alors que « Sans » est
 * coché et que 4.2 / Sans n'existe pas), Shopify ne résout aucune variante : prix à 0, « Non disponible »,
 * référence figée. Ce script corrige la sélection AVANT la requête d'Horizon (écouteur change en capture,
 * au niveau du document, donc exécuté avant celui du composant variant-picker) : les options du haut sont
 * gardées et les suivantes prennent les valeurs de la première variante existante, disponible de préférence.
 * Horizon lit ensuite l'état corrigé : une seule requête, sans état faux affiché.
 * Une valeur masquée n'étant pas dans la page, elle est portée par un bouton ou une option cachés
 * temporaires, remplacés au re-rendu d'Horizon.
 * Filet de sécurité : si la réponse d'Horizon n'a malgré tout aucune variante, la correction est rejouée
 * (3 fois au plus).
 * Données : <script data-perso-variantes> émis par snippets/variant-main-picker.liquid (absent en mode
 * « barrées » ou au-delà de 250 variantes : le script n'agit pas).
 */
import { StandardEvents } from '@shopify/events';

const MAX_CORRECTIONS = 3;

/** Corrections du filet de sécurité par sélecteur (pas d'attribut : le morph d'Horizon les effacerait) */
/** @type {WeakMap<Element, number>} */
const corrections = new WeakMap();

/**
 * @typedef {{ id: number, o: string[], d: boolean }} Variante
 * @typedef {{ options: string[], ids: Record<string, number>[], variantes: Variante[] }} Donnees
 */

/**
 * @param {Element} picker
 * @returns {Donnees | null}
 */
function donnees(picker) {
  const script = picker.querySelector('script[data-perso-variantes]');
  if (!script?.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

/**
 * Valeur choisie pour une option.
 * undefined : l'option n'a pas de sélecteur (valeur unique) ; null : la valeur choisie est masquée.
 * @param {Element} picker
 * @param {string} nom
 * @param {EventTarget | null} [declencheur] - la liste déroulante qui vient de changer (son attribut
 *   selected n'est mis à jour par Horizon qu'après cet écouteur)
 */
function valeurChoisie(picker, nom, declencheur = null) {
  const nomCss = CSS.escape(nom);
  const premier = picker.querySelector(`[data-option-name="${nomCss}"]`);
  if (!premier) return undefined;

  const select = premier.closest('select');
  if (select && select === declencheur) return select.value;

  const choisi = picker.querySelector(
    `input[data-option-name="${nomCss}"]:checked, option[data-option-name="${nomCss}"][selected]`
  );
  return choisi instanceof HTMLInputElement || choisi instanceof HTMLOptionElement ? choisi.value : null;
}

/**
 * Coche une valeur sans déclencher d'événement. Si elle n'est pas dans la page (masquée), un bouton ou une
 * option cachés la portent jusqu'au re-rendu d'Horizon.
 * @param {Element} picker
 * @param {string} nom
 * @param {string} valeur
 * @param {number | undefined} idValeur
 */
function cocher(picker, nom, valeur, idValeur) {
  const nomCss = CSS.escape(nom);
  const elements = [...picker.querySelectorAll(`[data-option-name="${nomCss}"]`)];
  const existant = elements.find(
    (el) => (el instanceof HTMLInputElement || el instanceof HTMLOptionElement) && el.value === valeur
  );
  const premier = elements[0];

  if (premier instanceof HTMLOptionElement) {
    const select = premier.closest('select');
    if (!select) return;
    for (const option of select.options) option.removeAttribute('selected');
    let option = existant instanceof HTMLOptionElement ? existant : null;
    if (!option) {
      if (idValeur === undefined) return;
      option = document.createElement('option');
      option.hidden = true;
      option.value = valeur;
      option.textContent = valeur;
      option.dataset.optionName = nom;
      option.dataset.optionValueId = String(idValeur);
      select.append(option);
    }
    option.setAttribute('selected', 'selected');
    select.value = valeur;
    return;
  }

  if (premier instanceof HTMLInputElement) {
    let input = existant instanceof HTMLInputElement ? existant : null;
    if (!input) {
      if (idValeur === undefined) return;
      input = document.createElement('input');
      input.type = 'radio';
      input.name = premier.name;
      input.value = valeur;
      input.hidden = true;
      input.tabIndex = -1;
      input.dataset.optionName = nom;
      input.dataset.optionValueId = String(idValeur);
      premier.closest('fieldset')?.append(input);
    }
    input.checked = true;
  }
}

/**
 * Remplace une combinaison inexistante par la meilleure combinaison existante.
 * @param {HTMLElement} picker
 * @param {EventTarget | null} [declencheur]
 * @returns {Variante | null} la variante visée, ou null si rien n'a été changé
 */
function corriger(picker, declencheur = null) {
  const infos = donnees(picker);
  if (!infos) return null;

  const choix = infos.options.map((nom) => valeurChoisie(picker, nom, declencheur));
  const correspond = (/** @type {string[]} */ o, /** @type {number} */ k) =>
    choix.slice(0, k).every((valeur, i) => valeur === undefined || valeur === o[i]);

  // Plus long début de combinaison qui existe
  let k = choix.length;
  while (k > 0 && !infos.variantes.some((v) => correspond(v.o, k))) k -= 1;
  if (k === choix.length) return null;

  const candidates = infos.variantes.filter((v) => correspond(v.o, k));
  const cible = candidates.find((v) => v.d) ?? candidates[0];
  if (!cible) return null;

  for (let i = k; i < choix.length; i += 1) {
    const nom = infos.options[i];
    const valeur = cible.o[i];
    if (choix[i] === undefined || choix[i] === valeur || nom === undefined || valeur === undefined) continue;
    cocher(picker, nom, valeur, infos.ids[i]?.[valeur]);
  }
  return cible;
}

// Avant Horizon : corrige la sélection au moment du changement
document.addEventListener(
  'change',
  (event) => {
    const cible = event.target;
    if (!(cible instanceof HTMLInputElement || cible instanceof HTMLSelectElement)) return;
    const picker = cible.closest('variant-picker');
    if (!(picker instanceof HTMLElement) || !picker.querySelector('script[data-perso-variantes]')) return;

    // Un vrai clic repart de zéro ; la relance du filet de sécurité (événement du script) garde le compteur
    if (event.isTrusted) corrections.delete(picker);
    const variante = corriger(picker, cible);
    if (!variante) return;

    // Horizon met l'URL (?variant=) à jour avec la variante portée par la valeur cliquée
    const porteur = cible instanceof HTMLSelectElement ? cible.options[cible.selectedIndex] : cible;
    if (porteur) porteur.dataset.variantId = String(variante.id);
  },
  true
);

// Filet de sécurité : réponse sans variante malgré tout
document.addEventListener(
  StandardEvents.productSelect,
  (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLElement) || !picker.matches('variant-picker')) return;
    if (!picker.querySelector('script[data-perso-variantes]')) return;

    const promesse = /** @type {{ promise?: Promise<any> }} */ (event).promise;
    if (!promesse) return;

    promesse
      .then(({ detail }) => {
        if (detail?.resource) return;
        const n = corrections.get(picker) ?? 0;
        if (n >= MAX_CORRECTIONS) return;
        corrections.set(picker, n + 1);
        if (corriger(picker)) {
          // Relance la requête d'Horizon avec la sélection corrigée
          picker.querySelector('input:checked, select')?.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch(() => {});
  },
  true
);
